import { selectRouterQueryParameters } from '@noths/polaris-client-next-redux';
import type { ParsedUrlQueryInput } from 'querystring';
import qs from 'querystring';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPathname } from '../../navigation/selectors';
import { getNextPageNumberNotLoaded, getPreviousPageNumberNotLoaded } from '../utils/pageNumbers';
import { selectFiltersWithActiveOptions } from './selectors';

export const selectFiltersAsUrlQuery = (state: ReduxApplicationState): ParsedUrlQueryInput => {
  const filtersWithActiveOptions = selectFiltersWithActiveOptions(state);

  return filtersWithActiveOptions.reduce((queryObj, filter) => {
    const optionsHaveUIDs = filter.options.some((option) => option.uid !== null);

    if (optionsHaveUIDs) {
      const activeOptionValues = filter.options.filter((option) => option.active);
      const activeOptionsObj = activeOptionValues.reduce((obj, option) => {
        return {
          ...obj,
          [option.uid!]: option.value,
        };
      }, {});

      return {
        ...queryObj,
        ...activeOptionsObj,
      };
    }

    return {
      ...queryObj,
      [filter.uid]: filter.options.filter((option) => option.active).map((option) => option.value),
    };
  }, {});
};

export const selectProductsQueryAsUrlQuery = (
  state: ReduxApplicationState,
  page?: number,
): ParsedUrlQueryInput => {
  const {
    productsQuery: { canonicalFilters, sortOptions = [] },
  } = state;

  const queryObject = selectFiltersAsUrlQuery(state);

  const { previewing } = selectRouterQueryParameters(state);

  if (previewing) {
    queryObject['previewing'] = true;
  }

  const activeSortOption = sortOptions.find((option) => option.active);

  if (activeSortOption) {
    queryObject['sort'] = activeSortOption.id;
  }

  if (page && page > 1) {
    queryObject['page'] = page;
  }

  if (canonicalFilters?.searchTerm) {
    queryObject['term'] = canonicalFilters.searchTerm;
  }

  return queryObject;
};

export const selectPreviousPageURL = (state: ReduxApplicationState): string | null => {
  const previousPageNumber = getPreviousPageNumberNotLoaded(state.products.pages);

  if (previousPageNumber === null) {
    return null;
  }

  const activeSortAndFilterQuery = selectProductsQueryAsUrlQuery(state, previousPageNumber);
  const pathname = selectPathname(state);
  const queryString = qs.stringify(activeSortAndFilterQuery);

  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
};

export const selectNextPageURL = (state: ReduxApplicationState): string | null => {
  const nextPageNumber = getNextPageNumberNotLoaded(
    state.products.pages,
    state.products.totalPages,
  );

  if (nextPageNumber === null) {
    return null;
  }

  const activeSortAndFilterQuery = selectProductsQueryAsUrlQuery(state, nextPageNumber);
  const pathname = selectPathname(state);
  const queryString = qs.stringify(activeSortAndFilterQuery);

  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
};
